// Compact Department Overview for All-in-One Dashboard View

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDepartmentStats } from '../../stores/dashboardStore';
import { useWorkshopStore } from '../../stores/workshopStore';

const CompactDepartmentOverview: React.FC = () => {
  const { departmentStats, isLoading } = useDepartmentStats();
  const navigate = useNavigate();
  const { setFilters, fetchWorkshops } = useWorkshopStore();

  // Navigation handler for department cards
  // Preserves the startup date filter by NOT calling clearFilters()
  const handleDepartmentClick = async (departmentId: number) => {
    // Set department filter while preserving date filters, clear other filters
    setFilters({ department: departmentId, statuses: undefined, status: undefined, overdue: undefined });
    await fetchWorkshops();
    navigate('/workshops');
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-4 gap-1">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg p-2 border shadow-sm animate-pulse">
            {/* Header skeleton */}
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="h-4 w-20 bg-gray-200 rounded mb-1"></div>
                <div className="h-2 w-12 bg-gray-200 rounded"></div>
              </div>
              <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
            </div>
            
            {/* Completion rate skeleton */}
            <div className="mb-2">
              <div className="flex justify-between items-center mb-1">
                <div className="h-2 w-16 bg-gray-200 rounded"></div>
                <div className="h-3 w-6 bg-gray-200 rounded"></div>
              </div>
              <div className="w-full h-1.5 bg-gray-200 rounded"></div>
            </div>
            
            {/* Stats skeleton */}
            <div className="grid grid-cols-3 gap-1 mb-2">
              <div className="text-center bg-gray-100 rounded-md py-2 px-1">
                <div className="h-5 w-5 bg-gray-200 rounded mx-auto mb-1"></div>
                <div className="h-2 w-10 bg-gray-200 rounded mx-auto"></div>
              </div>
              <div className="text-center bg-gray-100 rounded-md py-2 px-1">
                <div className="h-5 w-5 bg-gray-200 rounded mx-auto mb-1"></div>
                <div className="h-2 w-6 bg-gray-200 rounded mx-auto"></div>
              </div>
              <div className="text-center bg-gray-100 rounded-md py-2 px-1">
                <div className="h-5 w-5 bg-gray-200 rounded mx-auto mb-1"></div>
                <div className="h-2 w-8 bg-gray-200 rounded mx-auto"></div>
              </div>
            </div>
            
            {/* Savings skeleton */}
            <div className="space-y-1">
              <div className="flex justify-between items-center bg-gray-100 rounded-md py-1.5 px-2">
                <div className="h-2 w-12 bg-gray-200 rounded"></div>
                <div className="h-3 w-8 bg-gray-200 rounded"></div>
              </div>
              <div className="flex justify-between items-center bg-gray-100 rounded-md py-1.5 px-2">
                <div className="h-2 w-12 bg-gray-200 rounded"></div>
                <div className="h-3 w-8 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!departmentStats || departmentStats.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        <p className="text-sm">No department data available</p>
      </div>
    );
  }

  // Color mapping for departments
  const colorMap: Record<string, string> = {
    'IE': 'bg-purple-500',
    'PROD': 'bg-pink-500', 
    'Production': 'bg-pink-500',
    'APS': 'bg-green-500',
    'LOG': 'bg-orange-500',
    'Logistics': 'bg-orange-500',
    'HR_TRAIN': 'bg-blue-500',
    'HR & Training': 'bg-blue-500',
    'HSE': 'bg-yellow-500',
    'Quality': 'bg-red-500',
    'Maintenance': 'bg-indigo-500',
  };

  // Transform real department data and filter out departments with 0 workshops
  const departments = departmentStats
    .filter(dept => dept.workshop_count > 0)
    .map((dept) => ({
    id: dept.id,
    name: dept.name,
    code: dept.code,
    workshops: dept.workshop_count,
    completed: dept.completed,
    ongoing: dept.workshop_count - dept.completed - dept.late,
    late: dept.late,
    estimatedSavings: dept.estimated_savings || 0,
    releasedSavings: dept.released_savings || 0,
    completionRate: dept.completion_rate || 0,
    color: colorMap[dept.code] || colorMap[dept.name] || 'bg-gray-500'
  }));

  return (
    <div className="grid grid-cols-4 gap-1">
      {departments.map((dept) => {
        const completionRate = dept.workshops > 0 ? Math.round((dept.completed / dept.workshops) * 100) : 0;
        
        // Format currency helper
        const formatCurrency = (value: number): string => {
          if (value >= 1000000) {
            return `${(value / 1000000).toFixed(1)}M€`;
          } else if (value >= 1000) {
            return `${(value / 1000).toFixed(0)}K€`;
          } else if (value > 0) {
            return `${Math.round(value)}€`;
          }
          return '0€';
        };

        return (
          <div 
            key={dept.code} 
            className="bg-white rounded-lg p-2 border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => handleDepartmentClick(dept.id)}
          >
            {/* Header with department name and workshop count */}
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-base font-semibold text-gray-900">{dept.name}</h3>
                <p className="text-xs text-gray-500 uppercase tracking-wide">{dept.code}</p>
              </div>
              <div className={`w-10 h-10 ${dept.color} rounded-lg flex items-center justify-center text-white font-bold text-base`}>
                {dept.workshops}
              </div>
            </div>

            {/* Completion Rate */}
            <div className="mb-2">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-600">Completion Rate</span>
                <span className="text-sm font-semibold text-gray-900">{completionRate}%</span>
              </div>
              
              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className={`h-1.5 rounded-full ${dept.color}`}
                  style={{ width: `${completionRate}%` }}
                ></div>
              </div>
            </div>
            
            {/* Workshop status stats */}
            <div className="grid grid-cols-3 gap-1 mb-2">
              <div className="text-center bg-green-50 rounded-md py-2 px-1">
                <div className="text-lg font-bold text-green-600">{dept.completed}</div>
                <div className="text-xs text-green-600 font-medium uppercase tracking-wide">COMPLETED</div>
              </div>
              <div className="text-center bg-red-50 rounded-md py-2 px-1">
                <div className="text-lg font-bold text-red-600">{dept.late}</div>
                <div className="text-xs text-red-600 font-medium uppercase tracking-wide">LATE</div>
              </div>
              <div className="text-center bg-blue-50 rounded-md py-2 px-1">
                <div className="text-lg font-bold text-blue-600">{dept.ongoing}</div>
                <div className="text-xs text-blue-600 font-medium uppercase tracking-wide">ACTIVE</div>
              </div>
            </div>
            
            {/* Savings information */}
            <div className="space-y-1">
              <div className="flex justify-between items-center bg-orange-50 rounded-md py-1.5 px-2">
                <span className="text-xs text-orange-700 font-medium uppercase tracking-wide">ESTIMATED</span>
                <span className="text-sm font-semibold text-orange-600">{formatCurrency(dept.estimatedSavings)}</span>
              </div>
              <div className="flex justify-between items-center bg-green-50 rounded-md py-1.5 px-2">
                <span className="text-xs text-green-700 font-medium uppercase tracking-wide">RELEASED</span>
                <span className="text-sm font-semibold text-green-600">{formatCurrency(dept.releasedSavings)}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CompactDepartmentOverview;
